import React from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaPlus, FaMinus, FaCheck } from "react-icons/fa";

/* ----------------------------------------------------------------------- */
/* Numeric helpers                                                         */
/* ----------------------------------------------------------------------- */

// Converts a German weight "bucket" label (e.g. "251–500 kg", "Bis 250 kg",
// "2–3,5 Tonnen") into a representative integer number of kilograms. Used
// ONLY to fill the real `item_weight` column (which requires an integer) —
// the original bucket label itself is always kept as-is inside
// `special_items` for the human-readable summary/audit.
export const bucketToKg = (str) => {
  if (!str || typeof str !== "string") return null;
  const isTon = /tonnen/i.test(str);
  const nums = (str.match(/[\d.,]+/g) || [])
    .map((n) => parseFloat(n.replace(/\./g, "").replace(",", ".")))
    .filter((n) => !isNaN(n));
  if (nums.length === 0) return null;
  const val = nums.length >= 2 ? (nums[0] + nums[1]) / 2 : nums[0];
  return Math.round(isTon ? val * 1000 : val);
};

// Maps the 4-way "Nur Transport / Nur Beladung / Nur Entladung / Beladung
// und Entladung" answer onto the 3-way "0 / 1 / 2" scale the rest of the
// site already stores in `load_helping` (see DynamicForm.jsx: helpCarry "0"
// = no help, "1" = partial, "2" = full).
export const mapServiceToHelpCarry = (service) => {
  if (!service || service === "Nur Transport") return "0";
  if (service === "Beladung und Entladung") return "2";
  return "1"; // Nur Beladung / Nur Entladung
};

export const HELP_CARRY_OPTIONS = [
  { value: "0", label: "Keine Hilfe nötig" },
  { value: "1", label: "Teilweise Hilfe" },
  { value: "2", label: "Volle Hilfe (Be- und Entladen)" },
];

/* ----------------------------------------------------------------------- */
/* Rehydration helpers (used when the customer clicks the edit pencil and  */
/* comes back to a wizard step — see DeliverySummary.jsx handleEditItems)  */
/* ----------------------------------------------------------------------- */

export const dictByAreaItem = (arr) => {
  const dict = {};
  (arr || []).forEach((i) => {
    if (i?.area && i?.item) dict[`${i.area}::${i.item}`] = i;
  });
  return dict;
};

export const dictByKey = (arr, field = "item") => {
  const dict = {};
  (arr || []).forEach((i) => {
    if (i?.[field]) dict[i[field]] = i;
  });
  return dict;
};

/* ----------------------------------------------------------------------- */
/* Small reusable controls (chip / pill style — easier to tap than a wall  */
/* of checkboxes, and the active state is much clearer at a glance)        */
/* ----------------------------------------------------------------------- */

const normalizeOption = (opt) => (typeof opt === "string" ? { value: opt, label: opt } : opt);

export const Counter = ({ value = 0, onChange, min = 0, max = 999 }) => (
  <div className="d-flex align-items-center gap-2">
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={() => onChange(Math.max(min, (Number(value) || 0) - 1))}
      disabled={Number(value) <= min}
    >
      <FaMinus />
    </Button>
    <span className="mx-2 fw-semibold" style={{ minWidth: 24, textAlign: "center" }}>
      {value || 0}
    </span>
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={() => onChange(Math.min(max, (Number(value) || 0) + 1))}
    >
      <FaPlus />
    </Button>
  </div>
);

export const SingleSelect = ({ name, options, value, onChange }) => (
  <div className="d-flex flex-wrap gap-2 mb-2">
    {options.map((raw) => {
      const opt = normalizeOption(raw);
      const active = value === opt.value;
      return (
        <Button
          key={`${name}-${opt.value}`}
          type="button"
          size="sm"
          variant={active ? "success" : "outline-secondary"}
          className="rounded-pill px-3"
          onClick={() => onChange(opt.value)}
        >
          {active && <FaCheck className="me-1" size={11} />}
          {opt.label}
        </Button>
      );
    })}
  </div>
);

export const MultiSelect = ({ name, options, values = [], onChange }) => {
  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };
  return (
    <div className="d-flex flex-wrap gap-2 mb-2">
      {options.map((raw) => {
        const opt = normalizeOption(raw);
        const active = values.includes(opt.value);
        return (
          <Button
            key={`${name}-${opt.value}`}
            type="button"
            size="sm"
            variant={active ? "success" : "outline-secondary"}
            className="rounded-pill px-3"
            onClick={() => toggle(opt.value)}
          >
            {active && <FaCheck className="me-1" size={11} />}
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
};

export const StepHeader = ({ title, subtitle }) => (
  <div className="mb-3">
    <h5 className="mb-1">{title}</h5>
    {subtitle && <div className="text-muted small">{subtitle}</div>}
  </div>
);

/* ----------------------------------------------------------------------- */
/* Cascading single-page layout                                            */
/* ----------------------------------------------------------------------- */

// A section only mounts once `show` is true, and fades/slides in — this is
// what makes the whole wizard feel like ONE continuous form where each
// answer reveals the next question, instead of a paginated "step 3/8"
// experience. Unmounting when `show` is false (rather than just hiding with
// CSS) also means going back and changing an earlier answer cleanly drops
// everything that depended on it, so the form never shows stale questions.
export const Section = ({ show, first = false, children }) => {
  if (!show) return null;
  return <div className={`wizard-section${first ? "" : " wizard-section-enter"}`}>{children}</div>;
};

// Thin visual separator between cascaded questions — replaces the old
// "Schritt 4 / 8" page break with a lightweight divider so the page still
// reads as a sequence of questions without feeling like separate pages.
export const SectionDivider = () => <hr className="wizard-section-divider" />;

// Sticky bottom bar with Back + primary action. Used once per wizard instead
// of a WizardNav on every step. `disabled` + `disabledHint` let the caller
// keep the button visible but non-functional (with an explanatory line)
// instead of hiding it, so the customer always sees how close they are to
// finishing rather than the button appearing/disappearing.
export const StickyActionBar = ({ onBack, onSubmit, submitLabel = "Weiter zur Buchung", disabled, disabledHint }) => {
  const { t } = useTranslation();

  return (
    <div className="wizard-sticky-bar">
      <Button
        variant="secondary"
        className="px-4 rounded-5 order-2 order-sm-1"
        onClick={onBack}
      >
        {t("dynamic_form.back")}
      </Button>
      <div className="text-end">
        {disabled && disabledHint && <div className="small text-muted mb-1">{disabledHint}</div>}
        <Button
          variant="secondary"
          className="px-4 rounded-5 order-1 order-sm-2"
          style={{ backgroundColor: "#ff6b00", border: "none" }}
          onClick={onSubmit} 
          disabled={disabled}
        >
          {t("dynamic_form.next")}
        </Button>
      </div>
    </div>
  );
};

export const WizardNav = ({ step, totalSteps, onBack, onNext, nextLabel, nextDisabled }) => {
  const { t } = useTranslation();

  return (
    <div className="d-flex justify-content-between align-items-center mt-4">
      <Button
        variant="secondary"
        className="px-4 rounded-5 order-2 order-sm-1"
        onClick={onBack}
      >
        {t("dynamic_form.back")}
      </Button>
      <div className="text-muted small">
        Schritt {step + 1} / {totalSteps}
      </div>
      <Button
        variant="secondary"
        className="px-4 rounded-5 order-1 order-sm-2"
        style={{ backgroundColor: "#ff6b00", border: "none" }}
        onClick={onNext}
        disabled={nextDisabled}
      >
        {nextLabel || "Weiter"}
      </Button>
    </div>
  );
};

// Small reusable "Hilfe beim Be-/Entladen" + conditional floor/elevator
// block, matching the exact semantics/labels already used by the rest of
// the site (see DynamicForm.jsx `helpCarry` / `beladungFloor` / ...), so the
// booking ends up in the same real columns (load_helping, loading_floor,
// loading_elevator, unloading_floor, unloading_elevator) instead of only
// living inside special_items.
export const HelpAndFloorsStep = ({
  helpCarry,
  setHelpCarry,
  pickupFloor,
  setPickupFloor,
  pickupElevator,
  setPickupElevator,
  deliveryFloor,
  setDeliveryFloor,
  deliveryElevator,
  setDeliveryElevator,
}) => (
  <>
    <StepHeader title="Benötigen Sie Hilfe beim Be- und Entladen?" />
    <SingleSelect name="help-carry" options={HELP_CARRY_OPTIONS} value={helpCarry} onChange={setHelpCarry} />

    {helpCarry && helpCarry !== "0" && (
      <Row className="mt-3">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <Form.Group>
            <Form.Label className="small text-muted">Etage am Ladeort</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={pickupFloor}
              onChange={(e) => setPickupFloor(e.target.value)}
              placeholder="0"
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            className="mt-2"
            label="Aufzug vorhanden"
            checked={pickupElevator}
            onChange={(e) => setPickupElevator(e.target.checked)}
          />
        </Col>
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label className="small text-muted">Etage am Zielort</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={deliveryFloor}
              onChange={(e) => setDeliveryFloor(e.target.value)}
              placeholder="0"
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            className="mt-2"
            label="Aufzug vorhanden"
            checked={deliveryElevator}
            onChange={(e) => setDeliveryElevator(e.target.checked)}
          />
        </Col>
      </Row>
    )}
  </>
);